import { extent } from 'd3-array';
import { ScaleBand } from 'd3-scale';

import { getBarSize } from './categorical-layout.js';
import { TOOLTIP_DISTANCE_THRESHOLD } from './groupedDistancePoints.js';
import { isVertical } from './layout.js';
import type { Point } from '../../core/types/point.js';
import type { DimensionXYPoint } from '../types/categorical-bar-chart-data.js';
import type {
  CategoricalValueScales,
  Layout,
} from '../types/categorical-bar-chart.js';
import type { CategoryThreshold } from '../types/threshold.js';

/**
 * Retrieve the closest point in a stacked chart considering thresholds
 * and hovering the corresponding dimension.
 *
 * @param layout - layout mode of the chart
 * @param mousePosition - mouse coordinate
 * @param closestDimensionPoints - closest dimension points
 * @param valueScale - value scale
 * @param categoryScale - category scale
 * @returns closest point to the mouse coordinate in a stacked chart
 */
export const findClosestPointInStackedChart = (
  layout: Layout,
  mousePosition: Point,
  closestDimensionPoints: DimensionXYPoint[],
  valueScale: CategoricalValueScales,
  categoryScale: ScaleBand<string>,
): DimensionXYPoint | undefined => {
  const [minValue, maxValue] = extent(
    closestDimensionPoints.map((point) => point.datapoint.value),
  );
  const isMixed = minValue && maxValue && minValue < 0 && maxValue > 0;

  const sortedDimensions = closestDimensionPoints.sort(
    (a, b) =>
      (a.datapoint.cumulativeValue ?? a.datapoint.value) -
      (b.datapoint.cumulativeValue ?? b.datapoint.value),
  );

  return sortedDimensions.find((point, index) => {
    const barSize = getBarSize(point.datapoint.value, valueScale);
    let barThinkness = 0;

    if (categoryScale) {
      barThinkness = categoryScale.bandwidth() / 2;
    }

    const threshold: CategoryThreshold = getThreshold(
      closestDimensionPoints.length - 1,
      index,
      isVertical(layout),
      isMixed as boolean,
    );

    const isHovering = isInThreshold(
      layout,
      mousePosition,
      point,
      barSize,
      barThinkness,
      threshold,
    );

    return isHovering ? point : undefined;
  });
};

const isInsideChart = (
  mousePosition: Point,
  minX: number,
  maxX: number,
  minY: number,
  maxY: number,
) =>
  mousePosition.x > minX &&
  mousePosition.x < maxX &&
  mousePosition.y > minY &&
  mousePosition.y < maxY;

const getThreshold = (
  length: number,
  index: number,
  isVertical: boolean,
  isMixed: boolean,
): CategoryThreshold => {
  const left = TOOLTIP_DISTANCE_THRESHOLD;
  const right = TOOLTIP_DISTANCE_THRESHOLD;
  const top = TOOLTIP_DISTANCE_THRESHOLD;
  const bottom = TOOLTIP_DISTANCE_THRESHOLD;
  if (length > 0) {
    // First dimension
    if (index === 0) {
      return isVertical
        ? { left, right, top: isMixed ? top : 0, bottom: 0 }
        : { left: 0, right: isMixed ? right : 0, top, bottom };
    }
    // Last dimension
    if (index > 0 && index === length) {
      return isVertical
        ? { left, right, top: top, bottom: isMixed ? 0 : bottom }
        : { left: isMixed ? 0 : left, right, top, bottom };
    }

    // Inner dimensions
    return isVertical
      ? { left, right, top: 0, bottom: 0 }
      : { left: 0, right: 0, top, bottom };
  }

  return { left, right, top, bottom };
};

const isInThreshold = (
  layout: Layout,
  mousePosition: Point,
  point: DimensionXYPoint,
  barSize: number,
  barThinkness: number,
  threshold: CategoryThreshold,
): boolean => {
  const isVertical = layout === 'vertical';
  const isPositive = point.datapoint.value > 0;
  if (isVertical && isPositive) {
    return isInsideChart(
      mousePosition,
      point.x - barThinkness - threshold.left,
      point.x + barThinkness + threshold.right,
      point.y - threshold.top,
      point.y + barSize + threshold.bottom,
    );
  } else if (isVertical && !isPositive) {
    return isInsideChart(
      mousePosition,
      point.x - barThinkness - threshold.left,
      point.x + barThinkness + threshold.right,
      point.y - barSize - threshold.bottom,
      point.y + threshold.top,
    );
  } else if (!isVertical && isPositive) {
    return isInsideChart(
      mousePosition,
      point.x + barSize - threshold.left,
      point.x + threshold.right,
      point.y - barThinkness - threshold.bottom,
      point.y + barThinkness + threshold.top,
    );
  } else {
    return isInsideChart(
      mousePosition,
      point.x - threshold.right,
      point.x - barSize + threshold.left,
      point.y - barThinkness - threshold.bottom,
      point.y + barThinkness + threshold.top,
    );
  }
};
